// igual ao core/src/components/modals/EditUserModal/Tabs/badesulInputs.js
// @ts-check

import { isUniaoEstavel, isCouple } from 'utils';
import {
  documentOptions,
  educationOptions,
  maritalStatus,
  nationality,
  occupation,
  pepOptions,
  genderOptions,
  race,
  countries,
  addressPrefix,
  ufs,
  municipalities,
} from './inputOptions';

/**
 * @typedef  {{
 *  name:string;
 *  value:string;
 *  maskType?: "cnpj" | "phone" | "cpf" | "date" | "cep" | "rg";
 *  type?: "checkbox" | "currency";
 *  size: "1/3" | "2/3" | "1" | "1/2" | "document";
 *  options?: string[] | { name: string, value: string | number }[];
 *  defaultOption?: string;
 *  optionsFromDatabase?: string;
 *  showIf?: (user:any, additionalData?: any) => boolean
 *  text?:string
 *  withCustomRender?: string
 *  notRequired?: boolean
 *  forceType?: 'string'
 * }} input
 */

const getCivilStatusValue = (businessUser) => {
  const civilStatus =
    businessUser?.user?.civilStatus?.parent?.value ?? businessUser?.user?.civilStatus?.value;

  return civilStatus;
};

// Casado ou em união estável (inclui variantes '*-em-uniao-estavel') → coleta
// cônjuge/companheiro(a) e Regime de Bens. isCouple trata o value cru quando o
// parent do CivilStatus não vem populado na resposta GraphQL.
const hasPartner = (businessUser) =>
  isCouple(getCivilStatusValue(businessUser));

/**
 * @typedef {{
 *  divider: boolean
 * }} divider
 */

/**
 * @type {{
 *  key: string,
 *  name: string,
 *  showStep: (businessUser: any)=> boolean
 *  handleGetNextStep: (businessUser: any) => string
 *  handleGetLastStep: (businessUser: any) => string
 *  inputs: (input | divider)[]
 *  component?: string
 *  mutationName: string,
 * }[]}
 */
const badesulMicrocreditoPartnerFormItems = [
  {
    key: 'data',
    name: 'Dados do(a) sócio',
    inputs: [
      {
        size: '1/2',
        value: 'fullName',
        name: 'Nome completo',
      },
      {
        size: '1/2',
        value: 'cpf',
        name: 'CPF',
        maskType: 'cpf',
      },
      {
        size: '1/2',
        name: 'Email',
        value: 'email',
      },
      {
        size: '1/2',
        name: 'Telefone',
        value: 'phoneNumber',
        maskType: 'phone',
      },
      {
        size: '1/2',
        name: 'Nome da mãe',
        value: 'motherName',
      },
      {
        size: '1/2',
        name: 'Nome do pai',
        value: 'fatherName',
      },
      {
        size: '1/3',
        value: 'civilStatusId',
        name: 'Estado Civil',
        optionsFromDatabase: 'civilStatuses',
      },
      {
        size: '1/3',
        value: 'gender',
        name: 'Sexo',
        options: genderOptions,
      },
      {
        size: '1/3',
        value: 'birthDate',
        name: 'Data de Nascimento',
        maskType: 'date',
      },
      {
        size: '1/2',
        name: 'Nacionalidade',
        value: 'nationality',
        options: nationality,
        defaultOption: 'Brasileira',
      },
      {
        size: '1/2',
        name: 'Naturalidade (Cidade/UF)*:',
        value: 'naturalityCity',
      },
      {
        size: '1/2',
        name: 'É naturalizado?',
        value: 'isNaturalized',
        type: 'checkbox',
      },
      {
        size: '1/2',
        name: 'País de Origem',
        value: 'countryOfOrigin',
        options: countries,
        showIf: (businessUser, { formValues: { isNaturalized } = {} } = {}) => !!isNaturalized,
      },
      { divider: true },
      {
        size: '1',
        name: 'É empregador individual?',
        value: 'isIndividualEmployer',
        type: 'checkbox',
      },
      {
        size: '1',
        name: 'Possui participação societária em outras empresas?',
        value: 'hasAnotherSociety',
        type: 'checkbox',
      },
      { divider: true },
      {
        size: '1',
        name: 'Escolaridade',
        value: 'education',
        options: educationOptions,
      },
      {
        size: '1/3',
        name: 'Raça',
        value: 'race',
        options: race,
      },
      {
        size: '2/3',
        name: 'Profissão',
        value: 'occupation',
        options: occupation,
        defaultOption: '1284', // Empresario
        forceType: 'string',
      },
      {
        size: '1/2',
        name: 'Renda Anual',
        value: 'annualIncomeValue',
        type: 'currency',
      },
      {
        size: '1/2',
        name: 'Data Base',
        value: 'annualIncomeBaseDate',
        options: [
          '31/12/2024',
          '31/12/2023',
          '31/12/2022',
          '31/12/2021',
          '31/12/2020',
          '31/12/2019',
          '31/12/2018',
          '31/12/2017',
          '31/12/2016',
          '31/12/2015',
          '31/12/2014',
          '31/12/2013',
          '31/12/2012',
          '31/12/2011',
          '31/12/2010',
        ],
      },
      {
        size: '1',
        name: 'PEP',
        value: 'pep',
        options: pepOptions,
      },
      {
        size: '1',
        name: 'Familiar PEP',
        value: 'familiaryPep',
        options: pepOptions,
      },
      {
        size: '1',
        name: 'Possui relação com Governo, Estado ou Badesul',
        value: 'relationshipWithGovernment',
        options: [
          { name: 'Sim', value: 'true' },
          { name: 'Não', value: 'false' },
        ],
      },
      {
        size: '1',
        name: 'Descrição da relação com Governo, Estado ou Badesul',
        value: 'relationshipWithGovernmentDescription',
        showIf: (_, { formValues }) => formValues?.relationshipWithGovernment === 'true',
      },
      { divider: true },
      {
        size: '1/2',
        name: 'Tipo de documento',
        value: 'documentType',
        options: documentOptions,
      },
      {
        size: '1/2',
        name: 'Número documento',
        value: 'documentNumber',
      },
      {
        size: '1/2',
        name: 'Órgão Emissor',
        value: 'documentIssuingBody',
      },
      {
        size: '1/2',
        name: 'Data de Emissão',
        value: 'documentDate',
        maskType: 'date',
      },
      {
        size: '1/2',
        name: 'Número de documento de Identidade',
        value: 'rgNumber',
        showIf: (businessUser, { formValues: { documentType } = {} } = {}) =>
          documentType && documentType !== 'CARTEIRA_DE_IDENTIDADE',
        maskType: 'rg',
      },
      {
        size: '1/2',
        name: 'Orgão Emissor',
        value: 'rgIssuingBody',
        showIf: (businessUser, { formValues: { documentType } = {} } = {}) =>
          documentType && documentType !== 'CARTEIRA_DE_IDENTIDADE',
      },
    ],
    showStep: () => true,
    handleGetLastStep: () => undefined,
    handleGetNextStep: (businessUser) => {
      if (hasPartner(businessUser)) {
        return 'partner';
      }

      if (businessUser?.user?.additionalInfo?.hasAnotherSociety) {
        return 'society';
      }

      return 'address';
    },
    mutationName: 'saveBusinessUser',
  },
  {
    key: 'partner',
    name: 'Cônjuge',
    handleGetLastStep: () => 'data',
    showStep: (businessUser) => hasPartner(businessUser),
    handleGetNextStep: (businessUser) => {
      if (businessUser?.user?.additionalInfo?.hasAnotherSociety) {
        return 'society';
      }

      return 'address';
    },
    inputs: [
      {
        size: '1/2',
        name: 'CPF',
        value: 'partner_cpf',
        maskType: 'cpf',
      },
      {
        size: '1/2',
        name: 'Nome',
        value: 'partner_fullName',
        showIf: (businessUser, { creatingCivilPartner } = {}) => {
          if (businessUser?.user?.civilPartner?.id) {
            return true;
          }
          if (creatingCivilPartner) {
            return true;
          }

          return false;
        },
      },
      {
        size: '1/2',
        name: 'Email',
        value: 'partner_email',
        showIf: (businessUser, { creatingCivilPartner } = {}) => {
          if (businessUser?.user?.civilPartner?.id) {
            return true;
          }
          if (creatingCivilPartner) {
            return true;
          }

          return false;
        },
      },
      {
        size: '1/2',
        name: 'Regime de Bens',
        value: 'maritalStatus',
        showIf: (businessUser, { creatingCivilPartner } = {}) => {
          if (businessUser?.user?.civilPartner?.id) {
            return true;
          }
          if (creatingCivilPartner) {
            return true;
          }
          return false;
        },

        options: maritalStatus,
      },
    ],
    mutationName: 'saveBusinessUser',
  },
  {
    key: 'society',
    name: 'Dados da sociedade',
    showStep: (businessUser) => businessUser?.user?.additionalInfo?.hasAnotherSociety,
    handleGetLastStep: (businessUser) => {
      if (hasPartner(businessUser)) {
        return 'partner';
      }

      return 'data';
    },
    handleGetNextStep: () => 'address',
    inputs: [],
    component: 'BadesulSocietyForm',
    mutationName: 'saveBusinessUser',
  },
  {
    key: 'address',
    name: 'Endereço',
    showStep: () => true,
    handleGetLastStep: (businessUser) => {
      if (businessUser?.user?.additionalInfo?.hasAnotherSociety) {
        return 'society';
      }

      if (hasPartner(businessUser)) {
        return 'partner';
      }

      return 'data';
    },
    handleGetNextStep: () => 'documents',
    component: 'BadesulAddress',
    inputs: [
      {
        size: '1/2',
        value: 'address_postalCode',
        name: 'CEP',
        maskType: 'cep',
      },
      {
        size: '1/2',
        value: 'address_district',
        name: 'Bairro',
      },
      {
        size: '1/3',
        value: 'address_prefix',
        name: 'Prefixo',
        options: addressPrefix,
      },
      {
        size: '2/3',
        value: 'address_street',
        name: 'Endereço',
      },
      {
        size: '1/3',
        value: 'address_number',
        name: 'Número',
      },
      {
        size: '2/3',
        value: 'address_complement',
        name: 'Complemento',
        notRequired: true,
      },
      {
        size: '1/3',
        value: 'address_state',
        name: 'Estado',
        options: ufs,
      },
      {
        size: '2/3',
        value: 'address_city',
        name: 'Cidade',
        options: municipalities,
      },
    ],
    mutationName: 'saveAddress',
  },
  {
    key: 'documents',
    name: 'Documentos',
    showStep: () => true,
    handleGetNextStep: () => 'documents',
    handleGetLastStep: () => 'address',
    inputs: [
      {
        size: 'document',
        value: 'idCard',
        name: 'Documento de identificação',
      },
      {
        size: 'document',
        value: 'proofOfIncome',
        name: 'Comprovante de Renda',
      },
      {
        size: 'document',
        value: 'proofOfAddress',
        name: 'Comprovante de Endereço',
      },
      {
        size: 'document',
        value: 'partnerIdCard',
        name: 'Documento de identificação do cônjuge',
        showIf: (businessUser) => hasPartner(businessUser),
      },
      {
        size: 'document',
        value: 'proofOfMariageOrDivorce',
        name: 'Certidão de casamento',
        showIf: (businessUser) =>
          getCivilStatusValue(businessUser) === 'casado' &&
          businessUser?.user.civilStatus?.value !== 'uniao-estavel',
      },
      {
        size: 'document',
        value: 'proofOfMariageOrDivorce',
        name: 'Averbação da separação ou divórcio',
        showIf: (businessUser) =>
          businessUser?.user.civilStatus?.value?.includes('divorciado') ||
          businessUser?.user.civilStatus?.value?.includes('separado') ||
          businessUser?.user.civilStatus?.value === 'desquitado',
      },
      {
        // Viúvo: certidão de óbito (mesmo tratamento de casado/divorciado). GYR-1332
        size: 'document',
        value: 'proofOfMariageOrDivorce',
        name: 'Certidão de óbito',
        showIf: (businessUser) => getCivilStatusValue(businessUser) === 'viuvo',
      },
      // GYR-1451: solteiro e união estável (incl. variantes) NÃO pedem upload de
      // documento comprobatório — o comprobatório é o Termo de estado civil gerado
      // e assinado em tela logo abaixo (withProof/TermCard). Casado, divorciado/
      // separado e viúvo continuam exigindo o documento real (certidão/averbação/óbito).
      {
        size: '1',
        value: 'withProof',
        name: 'Termo de estado civil',
        type: 'checkbox',
        // usa o value cru (não getCivilStatusValue): 'uniao-estavel' tem parent
        // 'casado', então o parent colapsaria a detecção de união estável aqui.
        showIf: (businessUser) => {
          const civilStatus = businessUser?.user?.civilStatus?.value;
          return civilStatus === 'solteiro' || isUniaoEstavel(civilStatus);
        },
        text: `Confirmo que li, aceito e concordo com as informações
descritas acima. Reconheço a assinatura deste termo como
válida e comprovante do estado civil do sócio adicionado.`,
        withCustomRender: 'TermCard',
      },
    ],
    mutationName: 'completeBusinessUser',
  },
];

export { badesulMicrocreditoPartnerFormItems };
